import { z } from 'zod';
import type { AISchemaField, UISchemaField } from '../types/index';

export const featureGridAISchema: AISchemaField[] = [
  { name: 'eyebrow', type: 'string', required: false, max: 30 },
  { name: 'headline', type: 'string', required: true, max: 80 },
  { name: 'subheadline', type: 'string', required: false, max: 200 },
  {
    name: 'features',
    type: 'array<object>',
    required: true,
    fields: [
      { name: 'title', type: 'string', required: true, max: 50 },
      { name: 'description', type: 'string', required: true, max: 160 },
      { name: 'icon', type: 'string', required: false, max: 30, hint: 'lucide icon name (optional)' },
      { name: 'image', type: 'image_url', required: false },
    ],
  },
];

export const featureGridUISchema: UISchemaField[] = [
  { name: 'eyebrow', type: 'text', label: 'Eyebrow', max: 30 },
  { name: 'headline', type: 'text', label: 'Headline', required: true, max: 80 },
  { name: 'subheadline', type: 'textarea', label: 'Subheadline', max: 200 },
  { name: 'features', type: 'feature_list', label: 'Features', required: true },
];

export const featureGridZodSchema = z.object({
  eyebrow: z.string().max(30).optional().nullable(),
  headline: z.string().min(1).max(80),
  subheadline: z.string().max(200).optional().nullable(),
  features: z
    .array(
      z.object({
        title: z.string().max(50),
        description: z.string().max(160),
        icon: z.string().max(30).optional().nullable(),
        image: z.string().optional().nullable(),
      }),
    )
    .min(1)
    .max(8),
});

export type FeatureGridContent = z.infer<typeof featureGridZodSchema>;

export const featureGridSample: FeatureGridContent = {
  eyebrow: 'Capabilities',
  headline: 'Built around the silicon.',
  subheadline: 'Every feature engineered to use Apple silicon to its fullest.',
  features: [
    {
      title: 'Adaptive performance',
      description: 'Workloads scale across cores automatically. No manual tuning.',
      icon: 'zap',
      image: null,
    },
    {
      title: 'Studio-grade I/O',
      description: 'Thunderbolt 5 throughout. Drive multiple 6K displays.',
      icon: 'cable',
      image: null,
    },
    {
      title: 'Silent under load',
      description: 'Vapor chamber cooling stays whisper-quiet at full tilt.',
      icon: 'wind',
      image: null,
    },
    {
      title: 'Battery that lasts',
      description: 'Up to 22 hours of real-world use on Atlas Air.',
      icon: 'battery',
      image: null,
    },
    {
      title: 'Built to ship',
      description: 'Configurations that deliver in days, not weeks.',
      icon: 'package',
      image: null,
    },
    {
      title: 'Repairable by design',
      description: 'User-replaceable storage and RAM on Pro and Mini.',
      icon: 'wrench',
      image: null,
    },
  ],
};
